import type { MeasurementValue, MeasurementKey, UnitSystem } from '@/types/measurements';
import { MEASUREMENT_LABELS, formatMeasurement } from '@/types/measurements';

/**
 * Format measurements as WhatsApp-friendly text with bold headings.
 */
export function formatMeasurementsForWhatsApp(
  measurements: Partial<Record<MeasurementKey, MeasurementValue>>,
  garmentName?: string,
  unit: UnitSystem = 'cm'
): string {
  const lines: string[] = ['*My FitAfrica Measurements*'];

  if (garmentName) {
    lines.push(`Garment: ${garmentName}`);
  }

  lines.push('');

  for (const [key, value] of Object.entries(measurements)) {
    if (!value || value.valueCm <= 0) continue;
    const label = MEASUREMENT_LABELS[key as MeasurementKey] ?? key;
    lines.push(`${label}: *${formatMeasurement(value.valueCm, unit)}*`);
  }

  lines.push('');
  lines.push('_Measured with FitAfrica_');
  lines.push('AI Body Measurements for African Clothing');

  return lines.join('\n');
}

/**
 * Open WhatsApp with pre-filled text.
 */
export function shareViaWhatsApp(text: string): void {
  const encoded = encodeURIComponent(text);
  window.open(`https://wa.me/?text=${encoded}`, '_blank');
}

/**
 * Share via the Web Share API (native share sheet).
 * Falls back to WhatsApp if Web Share is unavailable.
 */
export async function shareNative(data: {
  title?: string;
  text: string;
}): Promise<boolean> {
  if (navigator.share) {
    try {
      await navigator.share({
        title: data.title ?? 'My FitAfrica Measurements',
        text: data.text,
      });
      return true;
    } catch {
      // User cancelled or share failed
      return false;
    }
  }

  // Fallback to WhatsApp
  shareViaWhatsApp(data.text);
  return true;
}

/**
 * Share a measurement overlay image via the Web Share API (with file support).
 * Falls back to downloading the image if sharing isn't supported.
 */
export async function shareOverlayImage(
  blob: Blob,
  text = 'My FitAfrica body measurements'
): Promise<boolean> {
  const file = new File([blob], 'fitafrica-measurements.png', {
    type: 'image/png',
  });

  // Try Web Share API with files (supported on modern mobile browsers)
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: 'FitAfrica Measurements',
        text,
      });
      return true;
    } catch {
      // User cancelled
      return false;
    }
  }

  // Fallback: download the image
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'fitafrica-measurements.png';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}
